import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Flame, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { fetchProgressTimeline, type ProgressEventType } from "@/lib/progress";
import { listRecentSocialActivitiesInDb, type SocialActivityKind } from "@/lib/supabase/social";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useSupabaseSession } from "@/hooks/use-supabase-session";

function formatRelative(iso: string) {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "az önce";
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diff / 60_000));
  if (minutes < 1) return "az önce";
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
}

type UnifiedPracticeItem = {
  id: string;
  kindLabel: string;
  title: string;
  subtitle: string;
  occurredAt: string;
  metricLabel: string;
  metricValue: string;
  metricNumeric: number;
  durationMinutes: number | null;
  streakDays: number | null;
};

function toDateKey(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function computeLongestStreakFromDates(dateKeys: string[]) {
  const uniqueSorted = Array.from(new Set(dateKeys.filter((k) => /^\d{4}-\d{2}-\d{2}$/.test(k)))).sort((a, b) => a.localeCompare(b));
  if (!uniqueSorted.length) return 0;

  let longest = 1;
  let current = 1;

  for (let i = 1; i < uniqueSorted.length; i += 1) {
    const prev = new Date(`${uniqueSorted[i - 1]}T00:00:00.000Z`);
    const next = new Date(`${uniqueSorted[i]}T00:00:00.000Z`);
    const diffDays = Math.round((next.getTime() - prev.getTime()) / 86_400_000);
    if (diffDays === 1) current += 1;
    else current = 1;
    longest = Math.max(longest, current);
  }

  return longest;
}

const PRACTICE_EVENT_TYPES = new Set<ProgressEventType>([
  "meditation_completed",
  "breath_completed",
  "diet_completed",
  "ritual_completed",
]);

const PRACTICE_TASK_KINDS = new Set<SocialActivityKind>(["meditasyon", "nefes", "diyet", "rituel"]);

function eventTypeToKindLabel(eventType: string) {
  if (eventType === "meditation_completed") return "Meditasyon";
  if (eventType === "breath_completed") return "Nefes";
  if (eventType === "diet_completed") return "Diyet";
  if (eventType === "ritual_completed") return "Ritüel";
  return "Pratik";
}

function taskKindToKindLabel(taskKind: SocialActivityKind) {
  if (taskKind === "meditasyon") return "Meditasyon";
  if (taskKind === "nefes") return "Nefes";
  if (taskKind === "diyet") return "Diyet";
  if (taskKind === "rituel") return "Ritüel";
  return "Pratik";
}


export default function UserPanelRituelSerisi() {
  const { user, loading } = useSupabaseSession();

  const timelineQuery = useQuery({
    queryKey: ["progress-timeline-practices", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchProgressTimeline,
    staleTime: 10_000,
  });

  const circleActivitiesQuery = useQuery({
    queryKey: ["circle-practice-activities", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listRecentSocialActivitiesInDb(120),
    staleTime: 10_000,
  });

  const completedPractices = useMemo<UnifiedPracticeItem[]>(() => {
    const timelineItemsRaw = Array.isArray((timelineQuery.data as any)?.items) ? ((timelineQuery.data as any).items as any[]) : [];
    const timelineItems: UnifiedPracticeItem[] = timelineItemsRaw
      .filter((item) => PRACTICE_EVENT_TYPES.has(String(item?.eventType ?? "") as ProgressEventType))
      .map((item) => {
        const metadata = item?.metadata && typeof item.metadata === "object" && !Array.isArray(item.metadata) ? item.metadata : {};
        const durationRaw = Number((metadata as any)?.durationMinutes ?? (metadata as any)?.minutes ?? 0);
        const streakRaw = Number((metadata as any)?.streakDays ?? 0);
        const awardedXp = Math.max(0, Number(item?.awardedXp ?? 0) || 0);
        const eventType = String(item?.eventType ?? "");

        return {
          id: `timeline:${String(item?.id ?? "")}`,
          kindLabel: eventTypeToKindLabel(eventType),
          title: String(item?.source ?? "").trim() || String(item?.label ?? "Pratik tamamlandı"),
          subtitle: `${String(item?.label ?? "Pratik tamamlandı")} • Panel`,
          occurredAt: String(item?.occurredAt ?? ""),
          metricLabel: "Kazanılan XP",
          metricValue: `+${awardedXp}`,
          metricNumeric: awardedXp,
          durationMinutes: Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : null,
          streakDays: Number.isFinite(streakRaw) && streakRaw > 0 ? Math.round(streakRaw) : null,
        };
      })
      .filter((item) => item.metricNumeric > 0);

    const circleItemsRaw = Array.isArray(circleActivitiesQuery.data) ? circleActivitiesQuery.data : [];
    const circleItems: UnifiedPracticeItem[] = circleItemsRaw
      .filter((item) => item.authorUserId === user?.id && PRACTICE_TASK_KINDS.has(item.taskKind))
      .map((item) => {
        const points = Math.max(0, Number(item.taskPoints ?? 0) || 0);
        return {
          id: `circle:${item.id}`,
          kindLabel: taskKindToKindLabel(item.taskKind),
          title: item.title,
          subtitle: `${item.subtitle} • Çember`,
          occurredAt: item.createdAt,
          metricLabel: "Görev Puanı",
          metricValue: String(points),
          metricNumeric: 0,
          durationMinutes: null,
          streakDays: null,
        };
      })
      .filter((item) => Number(item.metricValue) > 0);

    return [...timelineItems, ...circleItems].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  }, [circleActivitiesQuery.data, timelineQuery.data, user?.id]);

  const totalScore = completedPractices.reduce((sum, item) => sum + (item.metricLabel === "Kazanılan XP" ? item.metricNumeric : 0), 0);
  const strongestStreak = computeLongestStreakFromDates(completedPractices.map((item) => toDateKey(item.occurredAt)));
  const completionScore = Math.min(100, completedPractices.length * 10);

  if (!loading && !user?.id) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">Bu sayfayı görmek için giriş yapmalısın.</div>;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-[#d7b6d8] bg-gradient-to-br from-[#fff7fc] via-[#f8edfa] to-[#f0e3f8] p-5 shadow-sm">
        <Link to="/panel/profil" className="inline-flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-semibold text-[#5a4464] hover:bg-white/60">
          <ArrowLeft className="h-4 w-4" />
          Sosyal Profile Dön
        </Link>

        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#2f1f3c]">Tamamlanan Pratikler</h1>
            <p className="mt-1 text-sm text-[#6f5a77]">Panelde ve çemberde tamamladığın meditasyon, nefes, diyet ve ritüeller burada listelenir.</p>
          </div>

          <div className="grid grid-cols-3 gap-2 rounded-2xl border border-[#d8bad7] bg-white/90 p-3 text-center">
            <div>
              <div className="text-xl font-black text-primary">{completedPractices.length}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Tamamlanan</div>
            </div>
            <div>
              <div className="text-xl font-black text-primary">{strongestStreak}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">En Uzun Seri</div>
            </div>
            <div>
              <div className="text-xl font-black text-primary">{totalScore}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Toplam Puan</div>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-white/70 bg-white/75 p-3">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold text-[#5a4464]">
            <span>Pratik Akış Skoru</span>
            <span>%{completionScore}</span>
          </div>
          <Progress value={completionScore} className="h-2 bg-[#efe3f3]" />
        </div>
      </section>

      <section className="space-y-3">
        {timelineQuery.isLoading || circleActivitiesQuery.isLoading ? (
          <Card className="rounded-2xl border border-[#eadcec] p-5 text-sm text-slate-500">Pratikler yükleniyor...</Card>
        ) : completedPractices.length ? (
          completedPractices.map((practice) => (
            <Card key={practice.id} className="rounded-3xl border border-[#eadcec] bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
                      <Sparkles className="mr-1 h-3.5 w-3.5" /> {practice.kindLabel}
                    </Badge>
                    <Badge variant="secondary">{formatRelative(practice.occurredAt)}</Badge>
                  </div>
                  <h2 className="mt-2 text-xl font-black text-[#2f1f3c]">{practice.title}</h2>
                  <p className="mt-1 text-sm text-[#6f5a77]">{practice.subtitle}</p>
                </div>

                <div className="rounded-2xl border border-[#e7d4ea] bg-[#f9f3fb] px-3 py-2 text-right">
                  <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{practice.metricLabel}</div>
                  <div className="text-2xl font-black text-primary">{practice.metricValue}</div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 text-sm text-[#5a4464]">
                <Flame className="h-4 w-4 text-[#e06d2f]" />
                <span>
                  {practice.durationMinutes ? `${Math.round(practice.durationMinutes)} dk` : "Süre bilgisi yok"}
                  {practice.streakDays ? ` • ${practice.streakDays} gün seri` : ""}
                </span>
              </div>
            </Card>
          ))
        ) : (
          <Card className="rounded-2xl border border-dashed border-[#dcc5e0] bg-[#fcf8fd] p-8 text-center">
            <h2 className="text-lg font-black text-[#2f1f3c]">Henüz tamamlanan pratik yok</h2>
            <p className="mt-1 text-sm text-[#6f5a77]">İlk meditasyon, nefes, diyet veya ritüel tamamlandığında burada görünecek.</p>
            <Link to="/panel/meditasyon" className="mt-4 inline-flex rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
              Pratiklere Git
            </Link>
          </Card>
        )}
      </section>
    </div>
  );
}
